/* global JSX */
// List
// ***********************************************************************************************************************
/// <reference types="react-scripts" />
import { Alert, Box, Grid } from "@mui/material";
import * as React from "react";
import { Base, IIdItem } from "ui/Scripts/source/framework/base";
import { IListCardData, IListHeaderLineData, IListValueLineData, ListCard, ListHeaderLine, ListValueLine } from "../ListLine";
import { LocalLoading } from "../../Loading/LocalLoading";

export type TGetListItemsResult<T> = {
    items: T[];
    selectedId: string;
    checkedIds: string[];
};

export interface IListUsingComponentState<T extends IIdItem> {
    items: T[];
    hasMore: boolean;
    selectedId: string;
    detailsItem: T;
    showEditDialog: boolean;
    editDialogId: string;
    editDialogHasPrevious: boolean;
    editDialogHasNext: boolean;
}

interface IListProps {
    intialized: boolean;
    isLoading: boolean;
    hasMore: boolean;
    globalLoadingInUse?: boolean;
    localLoadingDisabled?: boolean;
    header?: IListHeaderLineData;
    lines?: IListValueLineData[];
    totalLine?: IListValueLineData;
    customLines?: React.ReactNode[];
    lineDetails?: JSX.Element;
    noDataMessage?: string;
    noDataMessageThreshold?: number;
    cards?: IListCardData[];
    multiSelect?: boolean;
    multiSelectCheckBoxColIndex?: number;
    sortColumn: string;
    sortOrderIsAsc: boolean;
    doubleClickDisabled?: boolean;
    selectedId: string;
    checkedIds?: string[];
    selectedColumn?: string;
    height?: string;
    itemVisibleThreshold?: number;
    disableHoverAndSelectedColors?: boolean;
    onSetSelectedIds: (selectedId: string, checkedIds: string[]) => void;
    onSearchLines: (sortColumn: string, sortOrderIsAsc: boolean, nextPage: boolean) => void;
    onOpenLine?: (id: string) => void;
    onLongPress?: (id: string) => void;
    onColumnClick?: (column: string) => void;
    onItemVisibleToUser?: (id: string) => void;
}

export interface IListState {
    lineDetailsVisible: boolean;
}

export class List extends React.Component<IListProps, IListState> {
    private listDiv = React.createRef<HTMLDivElement>();
    private observer: IntersectionObserver;
    private lastClickedRowIndex: number = -1;
    
    // #region Observer for line visibility
    getItemVisibleThreshold = () => {
        return this.props.itemVisibleThreshold ?? 0.7;
    };

    intersectionCallback = (entries: IntersectionObserverEntry[], observer: IntersectionObserver) => {
        for (const entry of entries) {
            if (!entry.isIntersecting || entry.intersectionRatio < this.getItemVisibleThreshold()) continue;
            const line = entry.target as HTMLDivElement;
            if (!line) continue;
            const id = line.getAttribute("data-id");
            if (!id) return;
            //line.style.backgroundColor = "red";
            this.props.onItemVisibleToUser(id);
        }
    };

    createObserver = () => {
        if (!this.props.onItemVisibleToUser) return;
        this.observer = new IntersectionObserver(this.intersectionCallback, {
            root: this.listDiv.current,
            rootMargin: "0px",
            threshold: this.getItemVisibleThreshold()
        });
    };

    removeObserver = () => {
        if (!this.observer || !this.props.onItemVisibleToUser) return;
        this.observer.disconnect();
    };
    // #endregion Observer for line visibility
    
    constructor(props) {
        super(props);
        this.state = {
            lineDetailsVisible: false
        };
    }

    handleScrollSub = Base.debounce((obj: List) => {
        const props = obj.props;
        if (props.isLoading || !props.hasMore) return;
        if (Math.abs(obj.listDiv.current.scrollHeight - obj.listDiv.current.clientHeight - obj.listDiv.current.scrollTop) < 10) {
            props.onSearchLines(props.sortColumn, props.sortOrderIsAsc, true);
        }
    }, 100);
    
    handleScroll = (event) => {
        this.handleScrollSub(this);
    };

    componentDidMount(): void {
        this.listDiv.current.addEventListener("scroll", this.handleScroll);
        //Observer
        this.createObserver();
    }

    componentWillUnmount(): void {
        this.listDiv.current.removeEventListener("scroll", this.handleScroll);
        //Observer
        this.removeObserver();
    }

    static getListItems<T extends IIdItem>(stateItems: T[], stateSelectedId: string, stateCheckedIds: string[], dbItems: T[], resetItems: boolean, refreshList: boolean): TGetListItemsResult<T> {
        let items: T[];
        if (!resetItems && !refreshList) {
            const oldIds = {};
            for (let j = 0; j < stateItems.length; j++) {
                oldIds[stateItems[j].id] = true;
            }
            const oldItems = stateItems.slice(0);
            const newItems = dbItems.filter(i => !oldIds[i.id]);
            items = [...oldItems, ...newItems];
        } else {
            items = dbItems;
        }
        return {
            items,
            selectedId: items.findIndex(i => i.id === stateSelectedId) > -1 ? stateSelectedId : "",
            checkedIds: items.filter(i => stateCheckedIds.indexOf(i.id) > -1).map(i => i.id)
        };
    }

    static getSelectedIds = (selectedId: string, checkedIds: string[]): string[] => {
        let result = [];
        if (checkedIds && checkedIds.length > 0) {
            result = checkedIds.slice(0);
        } else if (selectedId) {
            result.push(selectedId);
        }
        return result;
    };

    static getShowEditDialogStateChange = <T extends IIdItem, K extends keyof IListUsingComponentState<T>>(state: IListUsingComponentState<T>, id: string): Pick<IListUsingComponentState<T>, K> => {
        const isNew = id === Base.emptyGuid;
        const index = isNew
            ? 0
            : state.items.findIndex(i => i.id === id);
        if (index < 0) return {} as Pick<IListUsingComponentState<T>, K>;
        return {
            showEditDialog: true,
            editDialogId: id,
            editDialogHasPrevious: index > 0,
            editDialogHasNext: isNew ? false : index < state.items.length - 1 || state.hasMore,
            selectedId: isNew ? state.selectedId : id,
            detailsItem: isNew ? null : state.items[index]
        } as unknown as Pick<IListUsingComponentState<T>, K>;
    };

    static getEditDialogItemByIndexStateChange = <T extends IIdItem, K extends keyof IListUsingComponentState<T>>(state: IListUsingComponentState<T>, index: number, searchItems: () => void): Pick<IListUsingComponentState<T>, K> => {
        if (index < 0) return {} as Pick<IListUsingComponentState<T>, K>;
        if (index >= state.items.length) {
            if (!state.hasMore) return {} as Pick<IListUsingComponentState<T>, K>;
            searchItems();
            return {} as Pick<IListUsingComponentState<T>, K>;
        } else {
            return {
                selectedId: state.items[index].id,
                detailsItem: state.items[index],
                editDialogId: state.items[index].id,
                editDialogHasPrevious: index > 0,
                editDialogHasNext: index < state.items.length - 1 || state.hasMore
            } as unknown as Pick<IListUsingComponentState<T>, K>;
        }
    };

    static moveToPrevious = <T extends IIdItem>(state: { selectedId: string, items: T[] }, setEditDialogItemByIndex: (index: number) => void): void => {
        if (!state.selectedId) return;
        const index = state.items.findIndex(i => i.id === state.selectedId);
        if (index < 0) return;
        setEditDialogItemByIndex(index - 1);
    };

    static moveToNext = <T extends IIdItem>(state: { selectedId: string, items: T[] }, setEditDialogItemByIndex: (index: number) => void): void => {
        if (!state.selectedId) return;
        const index = state.items.findIndex(i => i.id === state.selectedId);
        if (index < 0) return;
        setEditDialogItemByIndex(index + 1);
    };
    // #endregion generic operations

    // #region Header
    handleChangeSortColumn = (sortColumn: string) => {
        const props = this.props;
        const oldSortColumn = props.sortColumn;
        const sortOrderIsAsc = oldSortColumn === sortColumn ? !props.sortOrderIsAsc : true;
        if (props.onColumnClick) {
            props.onColumnClick(sortColumn);
        }
        props.onSearchLines(sortColumn, sortOrderIsAsc, false);
    };

    lineIsCheckable = (line: IListValueLineData): boolean => {
        return !line.unselectable && !line.columns.find(j => j.errors && j.errors.length > 0);
    };

    getIdsOnColumnCheckboxChange = (aSelectedId: string, lines: IListValueLineData[], checked: boolean) => {
        let selectedId = "";
        let checkedIds: string[] = [];
        if (checked && lines && lines.length) {
            const validLines = lines.filter(i => this.lineIsCheckable(i));
            checkedIds = validLines.map(i => {
                return i.id;
            });
            if (checkedIds.length > 0) {
                selectedId = checkedIds.includes(aSelectedId) ? aSelectedId : checkedIds[0];
            }
        }
        this.lastClickedRowIndex = -1;
        return {
            selectedId,
            checkedIds
        };
    };

    getIdsOnLineCheckboxChange = (aSelectedId: string, aCheckedIds: string[], id: string, checked: boolean) => {
        let selectedId = aSelectedId;
        const checkedIds = aCheckedIds ? aCheckedIds.slice(0) : [];
        const index = checkedIds.indexOf(id);
        if (checked && index < 0) {
            checkedIds.push(id);
        } else if (!checked && index > -1) {
            checkedIds.splice(index, 1);
        }
        if (!checked && id === selectedId) {
            selectedId = "";
        }
        if (!selectedId && checkedIds.length === 1) {
            selectedId = checkedIds[0];
        }
        return {
            selectedId,
            checkedIds
        };
    };

    handleChangeColumnCheckbox = (column: string, checked: boolean) => {
        const props = this.props;
        const result = this.getIdsOnColumnCheckboxChange(props.selectedId, props.lines, checked);
        props.onSetSelectedIds(result.selectedId, result.checkedIds);
    };
    // #endregion Header

    // #region Line
    handleLineClickMultiSelect = (id: string, isShift: boolean, isCtrl: boolean) => {
        const props = this.props;
        if (!id || !props.onSetSelectedIds) return;
        if (!props.lines || props.lines.length === 0) return;
        const index = props.lines.findIndex(i => i.id === id);
        const isCheckable = this.lineIsCheckable(props.lines[index]);
        let checkedIds = isCheckable ? [id] : [];
        if (isShift && this.lastClickedRowIndex >= 0 && index >= 0) {
            const startIndex = Math.min(this.lastClickedRowIndex, index);
            const endIndex = Math.max(this.lastClickedRowIndex, index);
            const newCheckedIds = props.lines.slice(startIndex, endIndex + 1).filter(i => this.lineIsCheckable(i)).map(i => i.id);
            if (isCtrl) {
                checkedIds = props.checkedIds ? props.checkedIds.slice(0) : [];
                for (const newCheckedId of newCheckedIds) {
                    if (newCheckedId === props.lines[this.lastClickedRowIndex].id) continue;
                    if (checkedIds.includes(newCheckedId)) {
                        checkedIds = checkedIds.filter(i => i !== newCheckedId);
                    } else {
                        checkedIds.push(newCheckedId);
                    }
                }
            } else {
                checkedIds = newCheckedIds;
            }
        } else if (isCtrl) {
            checkedIds = props.checkedIds ? props.checkedIds.slice(0) : [];
            if (checkedIds.includes(id)) {
                checkedIds = checkedIds.filter(i => i !== id);
            } else {
                if (isCheckable) {
                    checkedIds.push(id);
                }
            }
            this.lastClickedRowIndex = props.lines.findIndex(i => i.id === id);
        } else {
            this.lastClickedRowIndex = props.lines.findIndex(i => i.id === id);
        }
        const selectedId = checkedIds.includes(id) ? id : "";
        props.onSetSelectedIds(selectedId, checkedIds);
        this.setState({
            lineDetailsVisible: selectedId && selectedId !== props.selectedId
        });
    };

    handleLineClickSingleSelect = (id: string, _isShift: boolean, _isCtrl: boolean) => {
        const props = this.props;
        if (!id || !props.onSetSelectedIds) return;
        props.onSetSelectedIds(id, []);
        this.setState({
            lineDetailsVisible: id !== props.selectedId ? true : !this.state.lineDetailsVisible
        });
    };

    handleLineClick = (id: string, isShift: boolean, isCtrl: boolean) => {
        if (this.props.multiSelect) {
            this.handleLineClickMultiSelect(id, isShift, isCtrl);
        } else {
            this.handleLineClickSingleSelect(id, isShift, isCtrl);
        }
    };

    handleChangeLineCheckbox = (id: string, checked: boolean) => {
        const props = this.props;
        if (!props.onSetSelectedIds) return;
        const result = this.getIdsOnLineCheckboxChange(props.selectedId, props.checkedIds, id, checked);
        props.onSetSelectedIds(result.selectedId, result.checkedIds);
    };
    // #endregion Line

    render() {
        const props = this.props;
        const state = this.state;
        const hasTotalRow = props.intialized && !!props.lines && !!props.totalLine;
        return (
            <Box display="flex" flexDirection="column" flexGrow={2} sx={{ height: props.height || "100%", position: "relative" }}>
                {(!props.globalLoadingInUse && !props.localLoadingDisabled && (!props.intialized || props.isLoading)) && (
                    <LocalLoading />
                )}
                {props.header &&
                    <ListHeaderLine
                        classes={props.header.classes}
                        sortColumn={props.sortColumn}
                        sortOrderIsAsc={props.sortOrderIsAsc}
                        defaultChecked={props.header.defaultChecked}
                        columns={props.header.columns.filter(i => !Base.isNullOrUndefined(i) && !i.hidden).map(i => {
                            return {
                                style: i.style,
                                content: i.content,
                                size: i.size,
                                tooltip: i.tooltip,
                                column: i.column,
                                styleClass: i.styleClass
                            };
                        })}
                        multiSelectCheckBoxColIndex={props.multiSelectCheckBoxColIndex}
                        onClick={this.handleChangeSortColumn}
                        onCheckboxChange={props.multiSelect ? this.handleChangeColumnCheckbox : null}
                    />
                }
                <Box
                    sx={{
                        height: (props.height && props.header && props.height !== "auto")
                            ? "calc(100% - 39px)"
                            : props.height,
                        overflow: "hidden",
                        display: "flex",
                        flexDirection: "column",
                        flexGrow: 2,
                    }}
                >
                    <Box
                        ref={this.listDiv}
                        sx={{
                            overflow: "hidden",
                            overflowY: "auto",
                            width: "100%"
                        }}
                    >
                        {(props.intialized && !!props.noDataMessage && (!props.lines || props.lines.length < (props.noDataMessageThreshold ?? 1)) && (!props.cards || props.cards.length < (props.noDataMessageThreshold ?? 1))) &&
                            <Alert severity="info">{props.noDataMessage}</Alert>
                        }
                        {(props.intialized && props.lines
                            ? props.lines.map((line, i) =>
                                <ListValueLine
                                    key={line.id}
                                    observer={this.observer}
                                    classes={line.classes}
                                    columns={line.columns}
                                    id={line.id}
                                    selected={props.selectedId === line.id}
                                    checked={props.checkedIds && props.checkedIds.indexOf(line.id) > -1}
                                    details={props.selectedId === line.id && state.lineDetailsVisible ? props.lineDetails : null}
                                    unselectable={line.unselectable}
                                    hoverElement={line.hoverElement}
                                    style={line.style}
                                    disableHoverAndSelectedColors={props.disableHoverAndSelectedColors}
                                    multiSelectCheckBoxColIndex={props.multiSelectCheckBoxColIndex}
                                    onCheckboxChange={props.multiSelect && !line.unselectable ? this.handleChangeLineCheckbox : null}
                                    onClick={this.handleLineClick}
                                    onDoubleClick={!props.doubleClickDisabled ? props.onOpenLine : null}
                                    onLongPress={props.onLongPress}
                                />
                            )
                            : null
                        )}
                        {(props.intialized && props.cards && props.cards.length > 0 && (
                            <Grid
                                container
                                spacing={1}
                            >
                                {props.cards.map((card, i) =>
                                    <ListCard
                                        key={card.id}
                                        rows={card.rows}
                                        id={card.id}
                                        sm={card.sm}
                                        subTitle={card.subTitle}
                                        selected={props.selectedId === card.id}
                                        checked={props.checkedIds && props.checkedIds.indexOf(card.id) > -1}
                                        onCheckboxChange={props.multiSelect ? this.handleChangeLineCheckbox : null}
                                        onClick={(id) => this.handleLineClick(id, false, false)}
                                        onDoubleClick={props.onOpenLine}
                                        onLongPress={props.onLongPress}
                                    />
                                )}
                            </Grid>
                        ))}
                        {props.intialized && props.customLines && props.customLines.map((line) => line)}
                    </Box>
                </Box>
                {hasTotalRow &&
                    <Box display="flex" flexGrow={2} flexDirection="column" sx={{ width: "100%" }}>
                        <ListValueLine
                            key={props.totalLine.id}
                            observer={this.observer}
                            classes={props.totalLine.classes}
                            columns={props.totalLine.columns}
                            id={props.totalLine.id}
                            selected={false}
                            checked={false}
                            details={null}
                            unselectable={true}
                            hoverElement={null}
                            totalLine={true}
                            onCheckboxChange={null}
                            onClick={() => {}}
                            onDoubleClick={null}
                            onLongPress={null}
                        />
                    </Box>
                }
            </Box>
        );
    }
}